import { useEffect, useMemo, useRef, useState } from 'react';

import { EntityRefUtil, RecordState } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { useReactToPrint } from 'react-to-print';

import { useToast } from '@/components/ui/toaster';
import { getQRCodesQueryOptions } from '@/queries/assets';

interface Asset {
  id: string;
}

export function useAssetQRCodePrint(assets: Asset[]) {
  const toast = useToast();
  const [triggerPrint, setTriggerPrint] = useState(false);
  const printQrRef = useRef<HTMLDivElement>(null);

  const assetIds = useMemo(() => assets.map((asset) => asset.id), [assets]);

  const {
    data: qrCodes,
    refetch: fetchQRCodes,
    isFetching: isFetchingQRCodes,
  } = useQuery({ ...getQRCodesQueryOptions({ assetState: RecordState.Active, assetIds }), enabled: false });

  const printAssetQrCodes = useReactToPrint({
    documentTitle: `Gearbelt Asset QR Code${assets.length > 1 ? 's' : ''}`,
    contentRef: printQrRef,
    onAfterPrint: () => setTriggerPrint(false),
  });

  useEffect(() => {
    if (triggerPrint && !isFetchingQRCodes && qrCodes && qrCodes.length > 0) {
      printAssetQrCodes();
    }
  }, [triggerPrint, qrCodes, isFetchingQRCodes]);

  const assetQRCodes = useMemo(() => {
    return (
      qrCodes
        ?.filter((qrCode) => assetIds.includes(qrCode.asset?.id))
        .map((qrCode) => ({
          label: EntityRefUtil.createAssetLabel(qrCode.asset),
          qr: qrCode.value,
        })) ?? []
    );
  }, [qrCodes, assetIds]);

  async function handlePrint() {
    if (assets.length > 0 && !isFetchingQRCodes) {
      setTriggerPrint(false);
      try {
        const result = await fetchQRCodes();
        if (result.data && result.data.length > 0) {
          setTriggerPrint(true);
        } else if (result.isSuccess) {
          toast.info(`No QR code${assets.length > 1 ? 's' : ''} found for the selected asset${assets.length > 1 ? 's' : ''}.`);
        }
      } catch (error) {
        toast.error(`Failed to fetch QR code${assets.length > 1 ? 's' : ''}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  return {
    handlePrint,
    printQrRef,
    assetQRCodes,
    isFetching: isFetchingQRCodes,
  };
}
